import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
  StatusBar,
  ImageBackground,
  Modal,
  Image
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import { useFocusEffect } from '@react-navigation/native';
import { getPromoCornerData, isPromoVoucherCouponKegunaan } from '../services/promoCornerApi';
import SocialBannerFeed from '../components/promoSocial/SocialBannerFeed';

const { width, height } = Dimensions.get('window');

export default function BrowseScreen({ navigation }) {
  const dummyImage = 'https://dummyimage.com/600x400/e2e8f0/000000&text=+';
  const portraitDummyImage = 'https://dummyimage.com/450x600/e2e8f0/000000&text=+';

  // Promo data state
  const [promoData, setPromoData] = useState({
    items: [],
    loading: true,
  });
  const [alertModal, setAlertModal] = useState({
    visible: false,
    title: '',
    message: '',
  });
  // Empty Coupon Highlight
  const [couponHighlight, setCouponHighlight] = useState({
    visible: false,
    image: null
  });

  const showCustomAlert = (title, message) => {
    setAlertModal({
      visible: true,
      title,
      message,
    });
  };

  const hideCustomAlert = () => {
    setAlertModal({
      visible: false,
      title: '',
      message: '',
    });
  };

  // Check if user is logged in
  const checkAuthAndNavigate = async (screenName) => {
    try {
      const userToken = await AsyncStorage.getItem('userToken');
      const sessionId = await AsyncStorage.getItem('sessionId');
      
      if (!userToken || !sessionId) {
        showCustomAlert(
          'Login Required',
          'You need to login to access this feature. Please login to continue.'
        );
        return;
      }
      
      navigation.navigate(screenName);
    } catch (error) {
      console.error('Error checking authentication:', error);
      showCustomAlert(
        'Login Required',
        'You need to login to access this feature. Please login to continue.'
      );
    }
  };

  // Load promo data
  const loadPromoData = async () => {
    try {
      setPromoData(prev => ({ ...prev, loading: true }));
      const response = await getPromoCornerData();
      if (response.success) {
        setPromoData({
          items: response.data.items || [],
          loading: false,
        });
      } else {
        setPromoData({
          items: [],
          loading: false,
        });
      }
    } catch (error) {
      console.error('Error loading promo data:', error);
      setPromoData({
        items: [],
        loading: false,
      });
    }
  };

  const handlePromoDetailClick = (promo) => {
    if (promo.type === 'estamp') {
      checkAuthAndNavigate('Estamp');
    } else if (promo.type === 'voucher') {
      checkAuthAndNavigate('Coupons');
    }
  }

  const closeCouponHighlight = React.useCallback(() => {
    setCouponHighlight({
      visible: false,
      image: null
    });
  }, []);

  // Load data on component mount and when screen is focused
  useEffect(() => {
    loadPromoData();
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      loadPromoData();
    }, [])
  );

  const [fontsLoaded] = useFonts({
    'Poppins': require('../../assets/fonts/Poppins-Regular.ttf'),
    'Poppins-Medium': require('../../assets/fonts/Poppins-Medium.ttf'),
    'Poppins-SemiBold': require('../../assets/fonts/Poppins-SemiBold.ttf'),
    'Poppins-Bold': require('../../assets/fonts/Poppins-Bold.ttf'),
  });

  // Remove showAuthAlert function as we no longer need authentication restrictions

  if (!fontsLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <Text>Loading...</Text>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor="#FFFFFF" />
      
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* User Profile Section */}
        <ImageBackground
          source={require('../../assets/top-background.png')}
          style={styles.profileSection}
          resizeMode="cover"
        >
          <View style={styles.topHeaderRow}>
            <View style={styles.topHeaderRowMenu}>
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={() => navigation.navigate('Login')}
              >
                <Text style={styles.topHeaderRowMenuItem}>Masuk</Text>
              </TouchableOpacity>
              <TouchableOpacity
                activeOpacity={0.7}
                onPress={() => navigation.navigate('Register')}
              >
                <Text style={styles.topHeaderRowMenuItem}>Daftar</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity 
              style={styles.zclubLogoContainer}
              activeOpacity={0.7}
              onPress={() => checkAuthAndNavigate('MainTabs')}
            >
              <View style={styles.zclubLogo}>
                <Image
                  source={require('../../assets/zoolyfe-club-new-white.webp')}
                  style={{ width: '100%', maxWidth: 130, aspectRatio: 1.89 }}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.zclubLogoBottomText}>zoolyfe.club</Text>
              <View style={styles.zclubLogoIcon}>
                <Ionicons name="information-circle-outline" size={12} color="#FFFFFF" />
              </View>
            </TouchableOpacity>
          </View>
          <View style={styles.bottomHeaderRow}>
            <View style={styles.avatar}>
              <Ionicons name="person" size={28} color="#F8FAFC" />
            </View>
            <View style={styles.profileTextContainer}>
              <View style={styles.profileButtons}>
                <TouchableOpacity 
                  style={styles.notificationButton}
                  onPress={() => navigation.navigate('PreLoginScan')}
                >
                  <Ionicons name="scan-outline" size={24} color="#F8FAFC" />
                </TouchableOpacity>
                <TouchableOpacity 
                  style={styles.notificationButton}
                  onPress={() => checkAuthAndNavigate('Notification')}
                >
                  <Ionicons name="notifications-outline" size={24} color="#F8FAFC" />
                </TouchableOpacity>
              </View>
              <Text style={styles.profileGreeting}>Welcome to ZClub!</Text>
            </View>
          </View>

          {/* Points and Stamps Cards */}
          <View style={styles.cardsContainer}>
            <TouchableOpacity 
              style={styles.pointCard}
              activeOpacity={0.7}
              onPress={() => {
                checkAuthAndNavigate('Points');
              }}
            >
              <View style={styles.pointEllipseBackground} />
              <View style={styles.cardContent}>
                <View style={styles.pointCoinContainer}>
                  <Image 
                    source={require('../../assets/icons/z-point-solid.svg')} 
                    style={{ 
                      width: 14, 
                      height: 14, 
                      tintColor: "#FFFFFF"
                    }}
                    resizeMode="contain"
                  />
                </View>
                <Text style={styles.cardTitle}>Point Reward</Text>
                <Text style={styles.pointsValue}>0</Text>
                <Text style={styles.pointsUnit}>pt</Text>
              </View>
            </TouchableOpacity>
          

            <TouchableOpacity 
              style={styles.stampCard}
              activeOpacity={0.7}
              onPress={() => {
                checkAuthAndNavigate('Estamp');
              }}
            >
              <View style={styles.ellipseBackground} />
              <View style={styles.cardContent}>
                <View style={styles.stampContainer}>
                  <Ionicons name="star" size={10} color="#F8FAFC" />
                </View>
                <Text style={styles.cardTitle}>E-Stamp</Text>
                <Text style={styles.pointsValue}>0</Text>
                <Text style={styles.pointsUnit}>Stickers</Text>
              </View>
            </TouchableOpacity>
          </View>
        </ImageBackground>

        {/* Promo Corner Section */}
        <View style={styles.promoCornerSection}>
          <View style={styles.promoHeader}>
            <Text style={styles.promoCornerTitle}>Promo Info</Text>
          </View>

          <View style={styles.promoGrid}>
            <SocialBannerFeed
              isAuthenticated={false}
              onNeedLogin={() => {
                showCustomAlert(
                  'Login Required',
                  'You need to login to like or comment. Please login to continue.'
                );
              }}
            />
          
          {promoData.loading ? (
            <View style={styles.promoLoadingContainer}>
              <Text style={styles.promoLoadingText}>Loading promo...</Text>
            </View>
          ) : promoData.items.length > 0 ? (
                promoData.items.map((item, index) => {
                  const hideVoucherCouponText = isPromoVoucherCouponKegunaan(item);
                  return (
                    // <TouchableOpacity 
                    //   key={item.id}
                    //   style={styles.promoCard}
                    //   onPress={() => {
                    //     if(item.isEmptyCoupon) {
                    //       {console.log('Empty promo pressed')}
                    //       if((item.externalURL??"") != "") {
                    //         {window.open(item.externalURL, '_blank')}
                    //       } else {console.log('Do nothing for empty coupon without valid link')}
                    //     } else {
                    //       if (item.type === 'estamp') {
                    //         checkAuthAndNavigate('Estamp');
                    //       } else if (item.type === 'voucher') {
                    //         checkAuthAndNavigate('Coupons');
                    //       }
                    //     }
                    //   }}
                    //   disabled={item.isEmptyCoupon && (item.externalURL??"") == ""}
                    // >
                    //   <ImageBackground
                    //     source={item.image ? { uri: item.image } : {uri: dummyImage}}
                    //     style={styles.promoCardBackground}
                    //     imageStyle={styles.promoCardImage}
                    //     resizeMode="cover"
                    //   >
                    //     <View
                    //       style={[
                    //         styles.promoCardOverlay,
                    //         !item.image && { backgroundColor: item.backgroundColor },
                    //         hideVoucherCouponText && { backgroundColor: 'transparent' },
                    //       ]}
                    //     >
                    //       <View style={styles.promoCardContent}>
                    //         {!hideVoucherCouponText && (
                    //           <>
                    //             <View style={styles.promoCardHeader}>
                    //               <View style={styles.promoTypeIndicator}>
                    //                 <Ionicons 
                    //                   name={item.type === 'estamp' ? 'star' : 'gift'} 
                    //                   size={16} 
                    //                   color={item.textColor} 
                    //                 />
                    //                 <Text style={[styles.promoTypeText, { color: item.textColor }]}>
                    //                   {item.type === 'estamp' ? 'E-Stamp' : 'Voucher'}
                    //                 </Text>
                    //               </View>
                    //             </View>
                    //             <Text style={[styles.promoCardTitle, { color: item.textColor }]} numberOfLines={2}>
                    //               {item.title}
                    //             </Text>
                    //             <Text style={[styles.promoCardSubtitle, { color: item.textColor }]} numberOfLines={1}>
                    //               {item.subtitle}
                    //             </Text>
                    //           </>
                    //         )}
                    //       </View>
                    //     </View>
                    //   </ImageBackground>
                    // </TouchableOpacity>
                    <View
                      key={item.id}
                    >
                      {item.isEmptyCoupon ? (
                        <TouchableOpacity 
                          style={styles.promoCard}
                          onPress={() => {
                            if(URL.canParse(item.externalURL)) {
                              {window.open(item.externalURL, '_blank')}
                            } else {
                              setCouponHighlight({
                                visible: true,
                                image: item.highlightImage ? item.highlightImage : portraitDummyImage
                              })
                            }
                          }}
                        >
                          <Image 
                            source={item.image ? { uri: item.image } : {uri: dummyImage}}
                            style={{
                              ...styles.promoCardBackground,
                              ...styles.promoCardImage
                            }} 
                            resizeMode='cover'
                          />
                        </TouchableOpacity>
                      ) : (
                        <View 
                          style={styles.couponCard}
                        >
                          <TouchableOpacity 
                            style={styles.couponImageContainer}
                            onPress={() => handlePromoDetailClick(item)}
                          >
                            <Image 
                              source={item.image ? { uri: item.image } : {uri: dummyImage}}
                              style={styles.couponImage} 
                              resizeMode='cover'
                            />
                          </TouchableOpacity>
                          <View style={styles.couponContent}>
                            <Text style={styles.couponTitle}>{item.title}</Text>

                            <TouchableOpacity 
                              style={styles.couponButton}
                              onPress={() => handlePromoDetailClick(item)}
                            >
                              <Text style={styles.couponButtonText}>{`Lihat Detail`}</Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      )}
                    </View>
                  );
                })
              ) : null}
          </View>
        </View>

        <View style={styles.bottomSpacing} />
      </ScrollView>

      {/* Custom Alert Modal */}
      <Modal
        visible={alertModal.visible}
        transparent={true}
        animationType="fade"
        onRequestClose={hideCustomAlert}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>{alertModal.title}</Text>
            <Text style={styles.modalMessage}>{alertModal.message}</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={hideCustomAlert}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.loginButton]}
                onPress={() => {
                  hideCustomAlert();
                  navigation.navigate('Login');
                }}
              >
                <Text style={styles.loginButtonText}>Login</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Empty Coupon Highlight Modal */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={couponHighlight.visible}
        onRequestClose={()=>{}}
      >
        <View style={styles.modalOverlay}>
          <View 
            style={styles.modalContainerCoupon}
          >
            <View 
              style={{
                position: 'relative',
                height: 533,
              }}
            >
              <TouchableOpacity
                style={styles.modalCloseCoupon}
                onPress={closeCouponHighlight}
              >
                <Ionicons name="close" size={16} color="#000000" />
              </TouchableOpacity>
              <img 
                src={couponHighlight.image ?? dummyImage }
                style={{
                  height: '100%',
                  objectFit: 'cover'
                }} 
              />
            </View>
          </View>
        </View>
      </Modal>

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  profileSection: {
    position: 'relative',
    paddingTop: 12,
    height: 220,
    overflow: 'hidden',
  },
  headerContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'top',
    paddingTop: 30,
  },
  avatarContainer: {
    flexDirection: 'row',
    alignItems: 'top',
    flex: 1,
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(248, 250, 252, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  profileTextContainer: {
    flex: 1,
    flexDirection: 'column',
    gap: 4,
  },
  profileGreeting: {
    fontSize: 16,
    fontWeight: '600',
    color: '#F8FAFC',
    fontFamily: 'Poppins-SemiBold',
  },
  profileButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 12,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statItemLabel: {
    fontSize: 12,
    color: '#94A3B8',
    fontFamily: 'Poppins',
  },
  statItemValue: {
    fontSize: 14,
    fontWeight: '700',
    color: '#22D3EE',
    fontFamily: 'Poppins-Bold',
  },
  notificationButton: {
    width: 28,
    height: 28,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
  },
  cardsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 4,
    gap: 16,
  },
  pointCard: {
    flex: 1,
    backgroundColor: '#F59E0B',
    borderRadius: 12,
    padding: 8,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.05)',
    elevation: 8,
    width: 172,
    height: 36,
  },
  stampCard: {
    flex: 1,
    backgroundColor: '#22D3EE',
    borderRadius: 12,
    padding: 8,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.05)',
    elevation: 8,
    width: 172,
    height: 36,
  },
  ellipseBackground: {
    position: 'absolute',
    width: 213,
    height: 213,
    borderRadius: 106.5,
    borderWidth: 24,
    borderColor: '#FFFFFF',
    opacity: 0.24,
    top: 0,
    left: 0,
	  transform: 'translateX(-45%) translateY(-37%)',
  },
  cardContent: {
    // flex: 1,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  cardTitle: {
    color: '#F8FAFC',
    fontSize: 12,
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
    lineHeight: 16,
  },
  pointEllipseBackground: {
    position: 'absolute',
    width: 213,
    height: 213,
    borderRadius: 106.5,
    borderWidth: 24,
    borderColor: '#FFFFFF',
    opacity: 0.24,
    top: 0,
    left: 0,
	  transform: 'translateX(-45%) translateY(-37%)',
  },
  coinContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  coin: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pointCoinContainer: {
    width: 18,
    height: 18,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.24)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 9,
  },
  pointCoin: {
    width: 10,
    height: 10,
    borderRadius: 12,
    backgroundColor: '#FEF3C7',
    borderWidth: 2,
    borderColor: '#FBBF24',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 4,
  },
  stampContainer: {
    width: 18,
    height: 18,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.24)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'baseline',
    alignSelf: 'stretch',
    gap: 4,
    marginTop: 36,
  },
  cardLabel: {
    color: '#F8FAFC',
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Poppins',
    marginBottom: 2,
    lineHeight: 16,
  },
  pointsDisplay: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  pointsValue: {
    color: '#F8FAFC',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'Poppins-SemiBold',
    marginLeft: 'auto',
    marginRight: 4,
    lineHeight: 20,
  },
  pointsUnit: {
    color: '#F8FAFC',
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Poppins',
    lineHeight: 16,
  },

  promoCornerSection: {
    paddingHorizontal: 20,
    marginTop: 12,
    gap: 12,
    paddingBottom: 100
  },
  promoHeader: {
    flexDirection: 'col',
    alignItems: 'flex-start',
    gap: 4,
    position: 'relative'
  },
  promoCornerTitle: {
    fontSize: 18,
    fontWeight: '600',
    fontFamily: 'Poppins-Semibold',
    color: '#000000',
    lineHeight: 28,
  },

  lihatSemuaButton: {
    backgroundColor: '#F1F5F9',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  lihatSemuaText: {
    fontSize: 14,
    fontWeight: '500',
    fontFamily: 'Poppins-Medium',
    color: '#1E293B',
    lineHeight: 20,
  },
  promoSubtitle: {
    fontSize: 12,
    fontWeight: '400',
    fontFamily: 'Poppins',
    color: '#94A3B8',
    lineHeight: 16,
  },
  promoLoadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  promoLoadingText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#94A3B8',
  },
  promoGrid: {
    gap: 12,
  },
  promoCard: {
    width: '100%',
    borderRadius: 12,
    minHeight: 145,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
  },
  promoCardBackground: {
    flex: 1,
    minHeight: 145,
  },
  promoCardImage: {
    borderRadius: 12,
  },
  promoCardOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    padding: 16,
    borderRadius: 12,
  },
  promoCardContent: {
    flex: 1,
    justifyContent: 'space-between',
  },
  promoCardHeader: {
    marginBottom: 8,
  },
  promoTypeIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  promoTypeText: {
    fontSize: 10,
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
  },
  promoCardTitle: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    marginBottom: 4,
    lineHeight: 18,
  },
  promoCardSubtitle: {
    fontSize: 12,
    fontFamily: 'Poppins',
    fontWeight: '400',
    opacity: 0.9,
    lineHeight: 16,
  },
  promoEmptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  promoEmptyText: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#94A3B8',
    marginTop: 8,
    textAlign: 'center',
  },
  bottomSpacing: {
    height: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    margin: 20,
    minWidth: 280,
    maxWidth: 320,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#64748B',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#F1F5F9',
  },
  loginButton: {
    backgroundColor: '#22D3EE',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
    color: '#64748B',
  },
  loginButtonText: {
    fontSize: 14,
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
    color: '#FFFFFF',
  },

  topHeaderRow: {
    position: 'relative',
    width: '100%',
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  topHeaderRowMenu: {
    position: 'absolute',
    top: 0,
    left: 0,
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 16,
  },
  topHeaderRowMenuItem: {
    color: '#FFFFFF',
    fontFamily: 'Poppins-Medium',
    fontSize: 14,
    fontWeight: 50,
    lineHeight: 20,
  },
  bottomHeaderRow: {
    width: '100%',
    paddingVertical: 4,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  zclubLogoContainer: {
    position: 'relative',
    width: 130,
    marginHorizontal: 'auto',
  },
  zclubLogo: {
    width: 130,
  },
  zclubLogoBottomText: {
    width: '100%',
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: 12,
    color: '#FFFFFF',
    textAlign: 'center',
    letterSpacing: '5%',
  },
  zclubLogoIcon: {
    position: 'absolute',
    top: 0,
    right: '20%',
    width: 12,
    height: 12
  },

  couponCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    elevation: 3,
  },
  couponCardExpired: {
    backgroundColor: '#F9FAFB',
    opacity: 0.7,
  },
  couponImageContainer: {
    height: 145,
    backgroundColor: '#F1F5F9',
  },
  couponImagePlaceholder: {
    flex: 1,
    backgroundColor: '#E2E8F0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  couponImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  couponContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  couponBadges: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6,
  },
  couponTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#000000',
    fontFamily: 'Poppins-SemiBold',
    lineHeight: 16,
  },
  couponButton: {
    backgroundColor: '#1E293B',
    borderRadius: 6,
    padding: 8,
  },
  couponButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#FFFFFF',
    fontFamily: 'Poppins-Medium',
    lineHeight: 16,
  },

  modalContainerCoupon: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 0,
    overflow: 'hidden',
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
    height: 533,
  },
  modalCloseCoupon: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 24,
    height: 24,
    marginTop: 8,
    marginRight: 8,
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 'calc(infinity * 1px)',
  }
});